/**
 * THE WORK ORDER BUILDER — the screen that replaces the Word document.
 *
 * WO/OVAL/2026-27/OP003 of 30-06-2026 went out with an EMPTY quantity cell, so
 * the contract had no value at all, and a clause three paragraphs later
 * released "60% of the total contract value". That is the document this screen
 * exists to make impossible.
 *
 * Every refusal here is mirrored in the database, so the screen is the
 * courtesy and the constraint is the control:
 *
 *   quantity > 0                  wo_line_qty_positive
 *   each basis carries its field  wo_line_basis_complete
 *   milestones sum to 100%        fn_wo_recalc
 *   title, dates, PAN, value      fn_wo_assert_complete, at submit
 *
 * The deductions are NOT typed. TDS comes off the contractor's PAN, GST off
 * his registration, and both are shown as they are computed so the buyer can
 * see the arithmetic he is agreeing to.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  AlertTriangle, ArrowLeft, Info, Plus, Search, Trash2,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { dbMessage, useQuery, useSites, useVendors, useWorksPaymentTerms } from '@/lib/data'
import { useServiceSettings } from '../lib/settings'
import { supabase } from '@/lib/supabase'
import { rupees } from '@/lib/format'
import { visibleSites } from '@/lib/domain'
import {
  Alert, Card, Field, Input, Loading, Modal, Select, Textarea, useToast,
} from '@/components/ui'
import { AttachmentCollector, type PendingAttachment } from '@/components/docs/shared'
import { uploadWoPapers } from '../lib/papers'
import { WoPapers } from '../components/WoPapers'
import { WoTerms } from '../components/WoTerms'
import { serviceCan } from '../nav'

/** The marker that turns a dropdown into a way of adding to the master. */
const ADD_NEW = '__new__'

type Basis = 'MEASURED_ITEM' | 'AREA_RATE' | 'LUMPSUM' | 'DAY_RATE' | 'EQUIPMENT_HIRE'

const BASIS: { value: Basis; label: string; hint: string }[] = [
  { value: 'MEASURED_ITEM', label: 'Measured item (BOQ)',
    hint: 'A work item from the schedule of rates, paid on measured quantity.' },
  { value: 'AREA_RATE', label: 'Area / built-up rate',
    hint: 'One rate over a measured area. Say WHICH area — roof, built-up, carpet.' },
  { value: 'LUMPSUM', label: 'Lumpsum milestone',
    hint: 'A fixed sum released on a named stage. Milestones must total 100%.' },
  { value: 'DAY_RATE', label: 'Day rate / manpower',
    hint: 'Per head per day by trade. Billed from an attendance sheet.' },
  // 0214. Only on an equipment hire order, and nothing else goes on one.
  { value: 'EQUIPMENT_HIRE', label: 'Equipment hire',
    hint: 'Machines on hire, paid per day on site from the certified hire log sheet. A monthly rate is paid at one-thirtieth per day.' },
]

/**
 * A HIRE LINE'S DAYS AND DAY RATE — 0214.
 *
 * Never typed. Machines x expected period, in days (a month is 30), at the
 * quoted rate turned into a day rate. rpc_wo_save_lines does the same sum and
 * does not take the screen's word for it; this is so the buyer sees the
 * number he is agreeing to while he types.
 */
function hireDerived(l: { hire_units: string; hire_period: string; hire_rate_basis: string;
                          hire_quoted_rate: string }) {
  const per = l.hire_rate_basis === 'MONTH' ? 30 : 1
  const days = (Number(l.hire_units) || 0) * (Number(l.hire_period) || 0) * per
  const rate = Math.round((Number(l.hire_quoted_rate) || 0) / per * 100) / 100
  return { days, rate }
}

interface Line {
  key: string
  pricing_basis: Basis
  work_item_id: string
  description: string
  scope_text: string
  sac_code: string
  uom: string
  quantity: string
  rate: string
  gst_rate_percent: string
  trade: string
  headcount: string
  milestone_label: string
  milestone_pct: string
  area_basis: string
  /** 0214, equipment hire only. Quantity, rate and unit follow from these. */
  hire_units: string
  hire_rate_basis: string
  hire_quoted_rate: string
  hire_period: string
  with_operator: boolean
  fuel_by_oval: boolean
  /**
   * THE PAYMENT SCHEDULE OF A LUMPSUM LINE — 0176.
   *
   * Reported 22-09-2026 from WO-T13: there was one milestone box and one share
   * box, so "30% on foundation, 40% on superstructure, 30% on handover" had
   * nowhere to go. It had to be three separate LINES with three invented
   * quantities adding back to the real one — the bill of quantities and the
   * payment schedule forced into one document.
   *
   * They are two documents. This is the second one, and it belongs to the line
   * rather than to the order because a single order may mix a lumpsum line
   * with measured and day-rate lines (their own WO-T18 does exactly that), and
   * one schedule over the whole contract sum would then be meaningless.
   */
  milestones: Milestone[]
}

interface Milestone {
  key: string
  label: string
  pct: string
}

const blankMilestone = (): Milestone => ({
  key: Math.random().toString(36).slice(2), label: '', pct: '',
})

const blank = (): Line => ({
  key: Math.random().toString(36).slice(2),
  pricing_basis: 'MEASURED_ITEM', work_item_id: '', description: '',
  scope_text: '', sac_code: '', uom: '',
  quantity: '', rate: '', gst_rate_percent: '18',
  trade: '', headcount: '', milestone_label: '', milestone_pct: '', area_basis: '',
  hire_units: '', hire_rate_basis: 'MONTH', hire_quoted_rate: '', hire_period: '',
  with_operator: true, fuel_by_oval: true,
  milestones: [],
})

const n = (v: string) => Number(v) || 0

/**
 * THE PAYMENT SCHEDULE OF ONE LUMPSUM LINE — 0176.
 *
 * WHAT THIS REPLACES, AND WHY IT IS A PANEL AND NOT TWO BOXES
 *
 * There used to be a "Milestone" box and a "Share %" box, sitting in the grid
 * beside SAC and Unit as though a milestone were another property of the line
 * like its unit of measure. It is not. It is a LIST, it has to add to exactly
 * 100, and — since 0177 — it is the thing that releases the money. A list that
 * must balance needs to show its own arithmetic while you type, which two
 * loose boxes in a grid cannot do.
 *
 * So: a bordered block of its own, the running total shown against the target
 * on every keystroke, and the rupee value of each stage worked out as you go —
 * because "40%" means nothing to the man approving it and "₹5,04,900" means
 * everything.
 *
 * THE TOTAL IS SHOWN, NOT ENFORCED HERE. The Submit button is already gated on
 * the problems list, the database refuses a schedule that does not balance
 * (fn_wo_ms_assert_total, at commit), and a field that fights you while you are
 * halfway through typing 30/40/30 is a field people learn to hate. Tell them
 * where they are; stop them at the door.
 */
function MilestoneSchedule({ line, lineValue, total, onChange }: {
  line: Line
  lineValue: number
  total: number
  onChange: (ms: Milestone[]) => void
}) {
  const ms = line.milestones
  const set = (key: string, patch: Partial<Milestone>) =>
    onChange(ms.map((m) => (m.key === key ? { ...m, ...patch } : m)))
  const balanced = ms.length > 0 && Math.abs(total - 100) < 0.001
  const left = Math.round((100 - total) * 1000) / 1000

  return (
    <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-slate-700">How the lump sum is released</p>
          <p className="text-[11px] leading-snug text-slate-500">
            Name each stage and what share of this line it releases. Nothing is payable
            until the Resident Construction Manager certifies a stage complete — and a
            stage part-complete carries no payment, as the order says.
          </p>
        </div>
        <button type="button" className="btn-secondary btn-sm"
                onClick={() => onChange([...ms, blankMilestone()])}>
          <Plus className="h-4 w-4" /> Add a stage
        </button>
      </div>

      {ms.length === 0 ? (
        <p className="mt-3 rounded-md border border-dashed border-slate-300 bg-white px-3 py-2
                      text-xs text-slate-500">
          No stages yet. A lump sum with no milestones has no event that ever makes the
          money payable — add at least one. A single stage of 100% is perfectly proper for
          a small job: <em>&ldquo;the whole of the work, certified complete&rdquo;</em>.
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {ms.map((m, i) => (
            <li key={m.key} className="flex flex-wrap items-start gap-2">
              <span className="mt-2 w-5 shrink-0 text-xs tabular-nums text-slate-400">
                {i + 1}.
              </span>
              <div className="min-w-[180px] flex-1">
                <Input value={m.label} placeholder="Foundation and plinth complete"
                       onChange={(e) => set(m.key, { label: e.target.value })} />
              </div>
              <div className="w-24">
                <Input type="number" min="0.001" max="100" step="0.001" value={m.pct}
                       placeholder="%"
                       onChange={(e) => set(m.key, { pct: e.target.value })} />
              </div>
              <span className="mt-2 w-32 shrink-0 text-right text-xs tabular-nums text-slate-500">
                {n(m.pct) > 0 && lineValue > 0
                  ? rupees(Math.round(lineValue * n(m.pct)) / 100)
                  : '—'}
              </span>
              <button type="button"
                      className="btn-ghost btn-sm mt-0.5 text-slate-400 hover:text-red-600"
                      title="Remove this stage"
                      onClick={() => onChange(ms.filter((x) => x.key !== m.key))}>
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {ms.length > 0 && (
        <div className={`mt-3 flex flex-wrap items-baseline justify-between gap-2 border-t pt-2
                         text-sm ${balanced ? 'border-emerald-200' : 'border-amber-200'}`}>
          <span className={balanced ? 'font-medium text-emerald-700' : 'font-medium text-amber-700'}>
            {balanced
              ? 'The stages account for the whole of this line ✓'
              : left > 0
                ? `${left}% of this line is not yet released by any stage`
                : `${Math.abs(left)}% more than the line is worth`}
          </span>
          <span className={`tabular-nums ${balanced ? 'text-emerald-700' : 'text-amber-700'}`}>
            {Number(total.toFixed(3))}% of 100%
            {lineValue > 0 && <span className="ml-2 text-slate-400">
              {rupees(Math.round(lineValue * total) / 100)} of {rupees(lineValue)}
            </span>}
          </span>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// THE DRAFT, WHILE YOU ARE AWAY CHOOSING A WORK ITEM
//
// The schedule of rates has 545 entries and grows, so choosing one is a
// full-page search rather than a drop-down (see ScheduleOfRates.tsx). That
// means leaving this screen mid-sentence, and a half-written work order must
// survive the trip.
//
// A MODULE-LEVEL VARIABLE, not sessionStorage, and the reason matters:
// the collected papers are File objects. A File cannot be turned into JSON, so
// stashing the draft as text would have silently dropped the contractor's
// quotation on the way to the picker and back - the worst kind of bug, because
// the form would look right. Both screens live in the same lazily-loaded
// Service chunk, so a client-side navigation between them does not reload this
// module and the variable simply stays there.
//
// What it does NOT survive is a hard refresh or a closed tab, which is correct:
// an unsaved draft is unsaved, and pretending otherwise is how people stop
// pressing Save.
// ---------------------------------------------------------------------------
interface Draft {
  route: string
  siteId: string; vendorId: string; nature: string; title: string; scope: string
  quoteRef: string; quoteDate: string; startDate: string; endDate: string
  retention: string; dlp: string; advance: string; advRecovery: string
  paymentTerm: string; paymentX: string
  internalNote: string; lines: Line[]; papers: PendingAttachment[]
}

let parked: Draft | null = null

/**
 * Take the draft back — but only on the way back from the picker.
 *
 * `returning` is true when the URL carries ?picked= (an item was chosen) OR
 * ?resume= (the person pressed "Back without choosing"). Both are put there by
 * the schedule of rates and by nothing else, so arriving any other way means
 * this is a fresh form and anything still parked is stale.
 *
 * ?resume= was added 14-09-2026 after testing found the obvious hole: pressing
 * Back without choosing came home with no parameters at all, so the guard threw
 * the draft away and handed back an empty form. Choosing nothing must cost
 * nothing.
 *
 * Note this does NOT clear the draft on a successful read, and that is
 * deliberate: React.StrictMode mounts every component twice in development, so
 * a read-once-and-clear would hand the draft to the throwaway first mount and
 * give the real one an empty form. It is cleared instead by the effect below,
 * which runs once either way.
 */
function unpark(route: string, returning: boolean): Draft | null {
  if (!returning || !parked || parked.route !== route) {
    parked = null
    return null
  }
  return parked
}

/** The fourth character of a PAN is the holder's status. Same rule as fn_tds_pct_from_pan. */
function tdsFromPan(pan?: string | null): number | null {
  if (!pan) return null
  const p = pan.trim().toUpperCase()
  if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(p)) return null
  return 'PH'.includes(p[3]) ? 1 : 2
}

export default function WorkOrderBuilder() {
  const { id } = useParams()
  const editing = Boolean(id)
  const { profile } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const [params, setParams] = useSearchParams()

  // Coming back from the schedule of rates. Read on the first render, before
  // anything else can overwrite the fields. Either with an item (?picked) or
  // empty-handed (?resume) — both must find the form exactly as it was left.
  const pickedId = params.get('picked')
  const pickedLine = params.get('line')
  const resuming = params.get('resume') === '1'
  const [resumed] = useState<Draft | null>(
    () => unpark(id ?? 'new', Boolean((pickedId && pickedLine) || resuming)))

  const { data: allSites } = useSites()
  const { data: paymentTerms } = useWorksPaymentTerms()
  const { data: vendors } = useVendors()
  const { data: items } = useQuery<any>('work_items',
    () => supabase.from('work_items').select('*').eq('is_active', true).order('item_name'),
    [], { cacheable: false })

  /**
   * The SAC master. A DIFFERENT master from the schedule of rates, deliberately
   * and on the board's instruction: the schedule says what the work is and what
   * it costs, the SAC master says what the tax authority calls it.
   *
   * is_active filters out '9954' and '9985'. Those are four-digit HEADINGS and
   * are not valid on an invoice; they survive in the table only so the historic
   * schedule-of-rates migration still replays.
   */
  /**
   * Trades, for a day-rate line.
   *
   * It was a free text box with "Mason" as the placeholder, so the same trade
   * arrived as Mason, mason, MASON and Mistri and no report could group them.
   * A master plus an escape hatch, because the alternative — telling somebody
   * mid-order to go and ask an administrator to add "Rigger" — is how free-text
   * fields get reinvented.
   */
  const { data: trades, refresh: refreshTrades } = useQuery<any>('work_trades',
    () => supabase.from('work_trades').select('code, trade_name, sort_order')
      .eq('is_active', true).order('sort_order').order('trade_name'),
    [], { cacheable: false, idField: 'code' })

  const { data: sacs } = useQuery<any>('sac_codes',
    () => supabase.from('sac_codes').select('code, description, gst_rate_percent')
      .eq('is_active', true).order('code'),
    [], { cacheable: false, idField: 'code' })

  /**
   * Units come from the SAME uoms master the material side uses - 33 codes,
   * maintained on Master Data. Typing them free-hand is how one site ends up
   * measuring in SQFT, another in SFT and a third in "sq ft", and no report
   * can ever add them up.
   *
   * Note the master says SFT, not SQFT. If a code you need is missing - LS for
   * a lumpsum line is the obvious one - add it on Master Data > UOMs rather
   * than typing it here. It is a company master, not this screen's list.
   */
  const { data: uoms } = useQuery<any>('uoms',
    () => supabase.from('uoms').select('code, description, uom_class, sort_order')
      .eq('is_active', true).order('uom_class').order('sort_order').order('code'),
    [], { cacheable: false, idField: 'code' })

  const uomOptions = useMemo(
    () => (uoms as any[]).map((u) => ({
      value: u.code,
      label: u.description ? `${u.code} — ${u.description}` : u.code,
    })),
    [uoms])

  const [siteId, setSiteId] = useState(resumed?.siteId ?? '')
  const [vendorId, setVendorId] = useState(resumed?.vendorId ?? '')
  const [nature, setNature] = useState(resumed?.nature ?? 'WORKS_CONTRACT')
  const [title, setTitle] = useState(resumed?.title ?? '')
  const [scope, setScope] = useState(resumed?.scope ?? '')
  const [quoteRef, setQuoteRef] = useState(resumed?.quoteRef ?? '')
  const [quoteDate, setQuoteDate] = useState(resumed?.quoteDate ?? '')
  const [startDate, setStartDate] = useState(resumed?.startDate ?? '')
  const [endDate, setEndDate] = useState(resumed?.endDate ?? '')
  const [retention, setRetention] = useState(resumed?.retention ?? '10')
  const [dlp, setDlp] = useState(resumed?.dlp ?? '12')
  const [advance, setAdvance] = useState(resumed?.advance ?? '0')
  const [advRecovery, setAdvRecovery] = useState(resumed?.advRecovery ?? '20')
  const [paymentTerm, setPaymentTerm] = useState(resumed?.paymentTerm ?? '')
  const [paymentX, setPaymentX] = useState(resumed?.paymentX ?? '')
  const [internalNote, setInternalNote] = useState(resumed?.internalNote ?? '')
  const [lines, setLines] = useState<Line[]>(resumed?.lines ?? [blank()])
  /** The contractor's papers, collected here and uploaded once the order has a number. */
  const [papers, setPapers] = useState<PendingAttachment[]>(resumed?.papers ?? [])
  /** The saved order's status, when editing. Only the papers card needs it. */
  const [savedStatus, setSavedStatus] = useState('DRAFT')
  const [busy, setBusy] = useState(false)
  // Which line asked for a new trade, and what it is to be called.
  const [newTradeFor, setNewTradeFor] = useState<string | null>(null)
  const [newTradeName, setNewTradeName] = useState('')
  const [err, setErr] = useState<string | null>(null)

  // 0212: a NEW work order starts with the retention, defect liability and
  // advance recovery set on Service settings (10%, 12 months, 20% until
  // changed). Once only, only while each box still holds its starting number,
  // and never on a saved order or a form coming back from the picker.
  const svc = useServiceSettings()
  const [termsPrefilled, setTermsPrefilled] = useState(false)
  useEffect(() => {
    if (id || resumed || termsPrefilled || !svc.ready) return
    setTermsPrefilled(true)
    setRetention((v) => (v === '10' ? String(svc.woRetentionDefaultPct) : v))
    setDlp((v) => (v === '12' ? String(svc.woDlpDefaultMonths) : v))
    setAdvRecovery((v) => (v === '20' ? String(svc.woAdvanceRecoveryDefaultPct) : v))
  }, [id, resumed, termsPrefilled, svc.ready, svc.woRetentionDefaultPct, svc.woDlpDefaultMonths, svc.woAdvanceRecoveryDefaultPct])

  /** Today, as the date input wants it. Nothing may be quoted after today. */
  const today = useMemo(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }, [])

  const mySites = useMemo(
    () => visibleSites(allSites as any[], profile?.role, profile?.id),
    [allSites, profile?.role, profile?.id])

  const contractors = useMemo(
    () => (vendors as any[]).filter((v) => v.is_active && !v.is_placeholder)
      .sort((a, b) => String(a.company_name).localeCompare(String(b.company_name))),
    [vendors])

  const vendor = contractors.find((v) => v.id === vendorId)
  const site = (allSites as any[]).find((s) => s.id === siteId)
  const tds = tdsFromPan(vendor?.pan)
  const registered = Boolean(vendor?.is_gst_registered)
  const interState = Boolean(vendor?.state_name && site?.state_name
    && String(vendor.state_name).trim().toUpperCase() !== String(site.state_name).trim().toUpperCase())

  // ---- load, when editing a draft ----------------------------------------
  //
  // Skipped when we have just come back from the picker: what is on screen is
  // the person's half-finished work, and re-reading the saved row would throw
  // away everything they typed before going to look up an item.
  useEffect(() => {
    if (!id || resumed) return
    void (async () => {
      const { data: w } = await supabase.from('work_orders').select('*').eq('id', id).maybeSingle()
      if (!w) return
      setSavedStatus(String(w.status ?? 'DRAFT'))
      setSiteId(w.site_id); setVendorId(w.vendor_id); setNature(w.contract_nature)
      setTitle(w.title ?? ''); setScope(w.scope_summary ?? '')
      setQuoteRef(w.quotation_ref ?? ''); setQuoteDate(w.quotation_date ?? '')
      setStartDate(w.start_date ?? ''); setEndDate(w.completion_date ?? '')
      setRetention(String(w.retention_pct ?? 0)); setDlp(String(w.defect_liability_months ?? 0))
      setAdvance(String(w.advance_pct ?? 0)); setAdvRecovery(String(w.advance_recovery_pct ?? 0))
      // The dropdown holds the TEMPLATE; payment_terms is the resolved
      // sentence. Loading the resolved one would put "30 Days from Invoice"
      // into a list whose entry reads "X Days from GRN" and match nothing.
      // PoBuilder does exactly this, for exactly this reason.
      const tmpl = (paymentTerms as any[]).find((t) => t.id === w.payment_term_id)
      setPaymentTerm(tmpl?.term_text ?? w.payment_terms ?? '')
      setPaymentX(w.payment_term_x ?? '')
      setInternalNote(w.internal_note ?? '')
      const { data: ls } = await supabase.from('wo_line_items').select('*').eq('wo_id', id).order('sl_no')
      // 0176: the schedules of every lumpsum line, in one read rather than one
      // per line. An order rarely has more than a handful, but the builder is
      // opened on a phone at a site gate often enough to matter.
      const { data: mss } = await supabase.from('wo_line_milestones')
        .select('wo_line_id, sl_no, label, pct')
        .in('wo_line_id', (ls ?? []).map((l: any) => l.id))
        .order('sl_no')
      if (ls?.length) {
        setLines(ls.map((l: any) => ({
          key: l.id, pricing_basis: l.pricing_basis, work_item_id: l.work_item_id ?? '',
          description: l.description ?? '', scope_text: l.scope_text ?? '',
          sac_code: l.sac_code ?? '', uom: l.uom ?? '',
          quantity: String(l.quantity ?? ''), rate: String(l.rate ?? ''),
          gst_rate_percent: String(l.gst_rate_percent ?? 18),
          trade: l.trade ?? '', headcount: String(l.headcount ?? ''),
          milestone_label: l.milestone_label ?? '', milestone_pct: String(l.milestone_pct ?? ''),
          area_basis: l.area_basis ?? '',
          hire_units: l.hire_units == null ? '' : String(l.hire_units),
          hire_rate_basis: l.hire_rate_basis ?? 'MONTH',
          hire_quoted_rate: l.hire_quoted_rate == null ? '' : String(l.hire_quoted_rate),
          hire_period: l.hire_period == null ? '' : String(l.hire_period),
          with_operator: l.with_operator ?? true,
          fuel_by_oval: l.fuel_by_oval ?? true,
          milestones: ((mss ?? []) as any[])
            .filter((m) => m.wo_line_id === l.id)
            .map((m) => ({ key: `${l.id}-${m.sl_no}`, label: m.label ?? '',
                           pct: String(m.pct ?? '') })),
        })))
      }
    })()
  }, [id, resumed, paymentTerms])

  /**
   * PAYMENT TERMS — 0171, the same master the purchase order reads.
   *
   * Filtered to what a works contract can honour: Service Procurement has no
   * goods receipt and nothing is dispatched, so "100% Against GRN" is hidden
   * here and still offered on the PO. See useWorksPaymentTerms.
   */
  const chosenPaymentTerm = useMemo(
    () => (paymentTerms as any[]).find((t) => t.term_text === paymentTerm),
    [paymentTerms, paymentTerm])

  /**
   * What is stored and printed, with the agreed figure in place of the X. An
   * order reaching a contractor still reading "X Days from Invoice" would be
   * embarrassing at best. Copied in substance from PoBuilder, deliberately —
   * the two should behave identically because they look identical.
   */
  const resolvedPaymentTerm = useMemo(() => {
    if (!chosenPaymentTerm?.placeholder_label || !paymentX.trim()) return paymentTerm
    return paymentTerm.replace(/\bX\b/, paymentX.trim())
  }, [paymentTerm, chosenPaymentTerm, paymentX])

  /**
   * WHERE THE WORDS AND THE FIGURES DISAGREE — board, 21-09-2026: warn, do not
   * block.
   *
   * The order already carries retention and mobilisation advance as enforced
   * percentages that drive every running account bill. A term that states a
   * different one is a second, unenforced promise, and the contractor will
   * read the sentence while the system applies the figure.
   *
   * It does not refuse: a term may legitimately describe something the
   * structured fields do not. But nobody signs a contradiction unseen.
   */
  const termClash = useMemo(() => {
    const t = resolvedPaymentTerm
    if (!t) return null
    const pct = (re: RegExp) => {
      const m = t.match(re)
      return m ? Number(m[1]) : null
    }
    const saysRetention = pct(/(\d+(?:\.\d+)?)\s*%\s*retention/i)
    const saysAdvance = pct(/(\d+(?:\.\d+)?)\s*%\s*advance/i)
    if (saysRetention !== null && Math.abs(saysRetention - n(retention)) > 0.001) {
      return `The payment term says ${saysRetention}% retention, but this order `
        + `deducts ${n(retention)}% from every bill. The figure is what the bills `
        + `will actually apply — the term is what the contractor will read.`
    }
    if (saysAdvance !== null && Math.abs(saysAdvance - n(advance)) > 0.001) {
      return `The payment term says ${saysAdvance}% advance, but this order's `
        + `mobilisation advance is ${n(advance)}%. No advance can be paid beyond `
        + `what that figure allows, whatever the term says.`
    }
    return null
  }, [resolvedPaymentTerm, retention, advance])

  // ---- the arithmetic, live ----------------------------------------------
  const money = useMemo(() => {
    const value = lines.reduce((a, l) => a + Math.round(n(l.quantity) * n(l.rate) * 100) / 100, 0)
    const gst = registered
      ? lines.reduce((a, l) =>
          a + Math.round(n(l.quantity) * n(l.rate) * n(l.gst_rate_percent)) / 100, 0)
      : 0
    return { value, gst, total: value + gst,
             hasLumpsum: lines.some((l) => l.pricing_basis === 'LUMPSUM') }
  }, [lines, registered])

  /**
   * THE SCHEDULE TOTAL, PER LINE — 0176.
   *
   * It used to be one total across the whole ORDER, which was the old model
   * speaking: one milestone per line meant "the milestones" and "the lines"
   * were the same list. Now each lumpsum line has its own schedule, and each
   * one must reach 100% of THAT line on its own.
   */
  const msTotal = useCallback(
    (l: Line) => l.milestones.reduce((a, m) => a + n(m.pct), 0), [])

  // ---- what would stop this being submitted -------------------------------
  const problems = useMemo(() => {
    const p: string[] = []
    if (!siteId) p.push('Choose a site.')
    if (!vendorId) p.push('Choose a contractor.')
    if (!title.trim()) p.push('Give the work order a title.')
    if (!startDate || !endDate) p.push('Both a start date and a completion date are needed — "120 days from the date of effective" is what the old paper order said, and nobody could count from it.')
    if (startDate && endDate && endDate < startDate) p.push('The completion date is before the start date.')
    // A quotation you have not received yet cannot be the one you priced this
    // order from. The database refuses it too — trg_wo_quotation_date, 0125.
    if (quoteDate && quoteDate > today)
      p.push(`The quotation is dated ${quoteDate}, which is in the future. A quotation that has not arrived yet cannot be the one this order was priced from.`)
    if (vendorId && tds === null) p.push(`${vendor?.company_name ?? 'This contractor'} has no valid PAN on file, so the TDS rate cannot be worked out. Without a PAN it is 20%, and somebody has to decide that deliberately.`)
    if (lines.length === 0) p.push('A work order with no lines has no value.')
    // 0214: hire lines only on a hire order, and nothing else on one. The
    // database refuses both (rpc_wo_save_lines); this says so first.
    if (nature === 'EQUIPMENT_HIRE' && lines.some((l) => l.pricing_basis !== 'EQUIPMENT_HIRE'))
      p.push('This is an equipment hire order, so every line must be priced as "Equipment hire". Measured work, area rates, lump sums and day-rate gangs go on a separate work order.')
    if (nature !== 'EQUIPMENT_HIRE' && lines.some((l) => l.pricing_basis === 'EQUIPMENT_HIRE'))
      p.push('Equipment hire lines go on an equipment hire order. Set the nature of contract to "Equipment / vehicle hire", or raise a separate order for the machines.')
    if (chosenPaymentTerm?.placeholder_label && !paymentX.trim())
      p.push(`The payment term needs a figure — enter the `
             + `${String(chosenPaymentTerm.placeholder_label).toLowerCase()} agreed for this order.`)
    lines.forEach((l, i) => {
      const at = `Line ${i + 1}`
      if (!l.description.trim()) p.push(`${at}: say what the work is.`)
      if (!l.uom.trim()) p.push(`${at}: choose a unit.`)
      else if (uomOptions.length > 0 && !uomOptions.some((u) => u.value === l.uom))
        p.push(`${at}: "${l.uom}" is not in the UOM master. Choose one from the list, or add it on Master Data \u2192 UOMs.`)
      if (n(l.quantity) <= 0) p.push(`${at}: quantity must be more than zero. This is exactly what was blank on the paper order.`)
      if (n(l.rate) <= 0) p.push(`${at}: rate must be more than zero.`)
      // SAC is mandatory on every line. Rule 46 of the CGST Rules wants the
      // code on the document, and the first PDF went out with the column blank
      // because nothing here asked for it.
      if (!l.sac_code) p.push(`${at}: choose a SAC code.`)
      if (!l.work_item_id)
        p.push(`${at}: choose the work item from the schedule of rates.`)
      if (l.pricing_basis === 'MEASURED_ITEM' && !l.work_item_id)
        p.push(`${at}: choose the work item from the schedule of rates.`)
      if (l.pricing_basis === 'AREA_RATE' && !l.area_basis.trim())
        p.push(`${at}: say WHICH area is being measured — roof, built-up, carpet.`)
      if (l.pricing_basis === 'DAY_RATE' && (!l.trade.trim() || n(l.headcount) <= 0))
        p.push(`${at}: a day rate needs a trade and a headcount.`)
      if (l.pricing_basis === 'EQUIPMENT_HIRE') {
        if (n(l.hire_units) <= 0 || !Number.isInteger(n(l.hire_units)))
          p.push(`${at}: say how many machines — a whole number.`)
        if (n(l.hire_quoted_rate) <= 0)
          p.push(`${at}: enter the hire rate as quoted, per ${l.hire_rate_basis === 'MONTH' ? 'month' : 'day'}.`)
        if (n(l.hire_period) <= 0)
          p.push(`${at}: enter the expected hire period, in ${l.hire_rate_basis === 'MONTH' ? 'months' : 'days'}.`)
      }
      // 0176/0177. The schedule is now the thing that releases the money, so
      // it is checked line by line and in the same words the database uses.
      if (l.pricing_basis === 'LUMPSUM') {
        if (l.milestones.length === 0)
          p.push(`${at}: a lump sum is released against named stages. Add at least one milestone — without them there is no event that ever makes the money payable.`)
        l.milestones.forEach((m, j) => {
          if (!m.label.trim()) p.push(`${at}, milestone ${j + 1}: give the stage a name.`)
          if (n(m.pct) <= 0) p.push(`${at}, milestone ${j + 1}: give it a share of the line.`)
        })
        const t = msTotal(l)
        if (l.milestones.length > 0 && Math.abs(t - 100) > 0.001)
          p.push(`${at}: the milestones add up to ${Number(t.toFixed(3))}%, not 100%. Every rupee of a lumpsum line must be released by some milestone, or part of it can never be claimed and never be cancelled.`)
      }
    })
    return p
  }, [siteId, vendorId, title, startDate, endDate, quoteDate, today, tds, vendor, lines, money, uomOptions, msTotal, nature])

  /**
   * Adding a trade the master has never seen.
   *
   * It goes into work_trades, not just onto this line — the next person to need
   * a rigger finds one in the list. The code is derived the same way the
   * migration derives it, so a trade added here and a trade seeded there cannot
   * collide on different rules.
   */
  async function saveNewTrade() {
    const name = newTradeName.trim()
    if (!name) return
    const code = name.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '')
    setBusy(true)
    try {
      const dup = (trades as any[]).find(
        (t) => t.trade_name.toLowerCase() === name.toLowerCase())
      if (!dup) {
        const { error } = await supabase.from('work_trades')
          .insert({ code, trade_name: name, sort_order: 900 })
        if (error) throw new Error(error.message)
        await refreshTrades()
      }
      if (newTradeFor) setLine(newTradeFor, { trade: dup?.trade_name ?? name })
      toast.success(dup ? `${dup.trade_name} is already on the list.` : `${name} added.`)
      setNewTradeFor(null); setNewTradeName('')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not add that trade')
    } finally { setBusy(false) }
  }

  /**
   * What the LINES say the contractor brings — 'LABOUR_ONLY', 'WITH_MATERIALS',
   * 'MIXED' or 'UNKNOWN'.
   *
   * The same rule as fn_wo_material_basis, and deliberately advisory only. What
   * the order PRINTS is always the database's answer; this exists so that a
   * header saying "Works contract" over nothing but labour-only lines is caught
   * on the screen where it can still be corrected, instead of on the sheet the
   * contractor is holding.
   */
  const materialBasis = useMemo(() => {
    const subs = lines
      .map((l) => (items as any[]).find((x) => x.id === l.work_item_id)?.sub_category)
      .filter((x): x is string => Boolean(x))
    if (subs.length === 0) return 'UNKNOWN'
    const composite = subs.some((x) => x === 'Composite')
    const labour = subs.some((x) => x === 'Labour only')
    if (composite && labour) return 'MIXED'
    return composite ? 'WITH_MATERIALS' : 'LABOUR_ONLY'
  }, [lines, items])

  /** Null when the header and the lines agree, or when nothing can be said. */
  const natureClash = useMemo(() => {
    // 0214: hire is a fact about the arrangement, like manpower supply; the
    // lines cannot contradict it. The mixing rule is in `problems` instead.
    if (nature === 'EQUIPMENT_HIRE') return null
    if (nature === 'MANPOWER_SUPPLY' || materialBasis === 'UNKNOWN') return null
    if (nature === 'WORKS_CONTRACT' && materialBasis === 'LABOUR_ONLY')
      return 'Every line is a LABOUR ONLY item, but this says works contract. '
           + 'The order will print as a Labour Only Contract.'
    if (nature === 'PURE_LABOUR' && materialBasis === 'WITH_MATERIALS')
      return 'Every line is a WITH MATERIAL item, but this says pure labour. '
           + 'The order will print as a Works Contract.'
    if (nature === 'PURE_LABOUR' && materialBasis === 'MIXED')
      return 'Some lines carry material. The order will print as a Works Contract, '
           + 'part with materials and part labour only.'
    return null
  }, [nature, materialBasis])

  function setLine(key: string, patch: Partial<Line>) {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  }

  /**
   * 0214: change a hire line and carry its days, day rate and unit with it,
   * so the amount, the contract value and the checks all read the real sum.
   */
  function setHire(key: string, patch: Partial<Line>) {
    setLines((ls) => ls.map((l) => {
      if (l.key !== key) return l
      const next = { ...l, ...patch }
      if (next.pricing_basis !== 'EQUIPMENT_HIRE') return next
      const { days, rate } = hireDerived(next)
      return { ...next, uom: 'DAY', quantity: days ? String(days) : '', rate: rate ? String(rate) : '' }
    }))
  }

  function pickItem(key: string, itemId: string) {
    // The work item's own uom came from the same master, so it lands on a
    // valid option and the Select shows it selected rather than blank.
    const it = (items as any[]).find((x) => x.id === itemId)
    // 0214: a Plant & Equipment Hire item makes the line a hire line, with the
    // rate as quoted. Every other item takes the path below, unchanged.
    if (it?.default_nature === 'EQUIPMENT_HIRE') {
      const cur = lines.find((l) => l.key === key)
      setHire(key, {
        pricing_basis: 'EQUIPMENT_HIRE',
        work_item_id: itemId,
        description: it.item_name ?? '',
        sac_code: it.sac_code ?? '',
        gst_rate_percent: String(it.gst_rate_percent ?? 18),
        hire_rate_basis: it.hire_rate_basis ?? 'MONTH',
        hire_quoted_rate: it.hire_quoted_rate ? String(it.hire_quoted_rate) : '',
        hire_units: cur?.hire_units || '1',
        with_operator: it.sub_category === 'Without operator' ? false : true,
        // 0220: no fuel for a shuttering plate.
        ...(it.hire_by_pieces ? { with_operator: false, fuel_by_oval: false } : {}),
      })
      return
    }
    setLine(key, {
      work_item_id: itemId,
      description: it?.item_name ?? '',
      // NOT the scope text. The item decides what the work is called; what is
      // included on THIS order is typed by the buyer and must survive a change
      // of item, so scope_text is deliberately left alone here.
      uom: it?.uom ?? '',
      sac_code: it?.sac_code ?? '',
      gst_rate_percent: String(it?.gst_rate_percent ?? 18),
      rate: it?.standard_rate ? String(it.standard_rate) : '',
    })
  }

  /**
   * Choosing a SAC sets the line's GST rate from the master's slab.
   *
   * Only where the master states one unambiguously. Five of the codes carry a
   * conditional slab - affordable housing at 1%, 5%, 12% or 18% depending on
   * the promoter and the carpet area - and for those the master holds null and
   * this leaves the typed rate alone rather than guessing.
   */
  function pickSac(key: string, code: string) {
    const rate = (sacs as any[]).find((x) => x.code === code)?.gst_rate_percent
    setLine(key, {
      sac_code: code,
      ...(rate == null ? {} : { gst_rate_percent: String(rate) }),
    })
  }

  // ---- off to the schedule of rates, and back -----------------------------
  //
  // 545 items in a <select> is a scroll bar with no search. So the button
  // parks what is on this screen, sends the person to the register itself -
  // same table, same search, same measurement rule in the last column, which is
  // what you actually choose between two similar items on - and the row they
  // click brings them back here with it filled in.
  //
  // The draft is held in memory (see `parked` above) and survives BOTH ways
  // home: with an item, and with "Back without choosing". The one thing it does
  // not survive is a browser refresh or a closed tab, which is why an unsaved
  // form asks before it lets you leave. See `leaveTo` below.
  function goToPicker(lineKey: string) {
    parked = {
      route: id ?? 'new',
      siteId, vendorId, nature, title, scope, quoteRef, quoteDate,
      startDate, endDate, retention, dlp, advance, advRecovery,
      paymentTerm, paymentX,
      internalNote, lines, papers,
    }
    const back = encodeURIComponent(location.pathname)
    navigate(`/service/work-items?pick=${encodeURIComponent(lineKey)}&back=${back}`)
  }

  /**
   * Has anything been typed that would hurt to lose?
   *
   * Used only to decide whether to warn. Deliberately generous - a site and a
   * contractor chosen is already two decisions somebody made.
   */
  const hasContent = Boolean(
    siteId || vendorId || title.trim() || scope.trim() || quoteRef.trim()
    || startDate || endDate || papers.length
    || lines.some((l) => l.description.trim() || l.quantity || l.rate || l.work_item_id))

  /**
   * WARNING BEFORE A MULTI-PAGE TRIP, and why there is one at all.
   *
   * Choosing a work item leaves this page. The typing is carried across in
   * memory, so the round trip itself is safe - but the browser's own refresh
   * button, a closed tab, a dropped session or a laptop lid are not, and a
   * work order can take twenty minutes to write.
   *
   * Saying nothing and hoping is how somebody loses that twenty minutes once
   * and never trusts the screen again. So an unsaved order says so, in
   * so many words, and offers the safe path FIRST: save the draft, then go.
   * A draft that exists on the server cannot be lost by anything.
   *
   * The warning does not appear once the order has been saved - there is
   * nothing left to lose - so the people who write orders all day are not
   * nagged on every line.
   */
  const [leaveTo, setLeaveTo] = useState<string | null>(null)

  function chooseWorkItem(lineKey: string) {
    if (!id && hasContent) { setLeaveTo(lineKey); return }
    goToPicker(lineKey)
  }

  async function saveThenChoose() {
    const lineKey = leaveTo
    setLeaveTo(null)
    if (!lineKey) return
    const savedId = await save(false, { stay: true })
    if (!savedId) return           // it did not save; the reason is on screen
    parked = {
      route: savedId,
      siteId, vendorId, nature, title, scope, quoteRef, quoteDate,
      startDate, endDate, retention, dlp, advance, advRecovery,
      paymentTerm, paymentX,
      internalNote, lines, papers: [],
    }
    navigate(`/service/work-items?pick=${encodeURIComponent(lineKey)}`
             + `&back=${encodeURIComponent(`/service/work-orders/${savedId}/edit`)}`)
  }

  // The item they chose, applied once the master has loaded. The query rarely
  // takes long, but applying a pick against an empty list would silently blank
  // the line instead of filling it.
  useEffect(() => {
    if (!pickedId && !resuming) return
    if (pickedId && !(items as any[]).length) return
    if (pickedId && pickedLine) pickItem(pickedLine, pickedId)
    parked = null                    // the round trip is over; nothing to keep
    // Clear the parameters so a refresh, or a second visit, does not re-apply
    // a choice the person may since have changed.
    setParams({}, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedId, pickedLine, resuming, items])

  /**
   * Save it. Returns the order number on success and null on failure, so a
   * caller that has something to do NEXT — save-then-choose, below — can tell
   * the difference without reading the screen.
   *
   * `stay` keeps the person here instead of navigating to the saved order. It
   * is used by the picker path, which is going somewhere else of its own.
   */
  async function save(
    thenSubmit: boolean, opts?: { stay?: boolean },
  ): Promise<string | null> {
    setErr(null)
    if (problems.length) {
      setErr('Fix the points listed below first.')
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return null
    }
    setBusy(true)
    try {
      let woId = id
      if (!woId) {
        const { data: newId, error: idErr } = await supabase.rpc('rpc_next_wo_id', { p_site_id: siteId })
        if (idErr) throw new Error(idErr.message)
        woId = newId as string
      }

      const header = {
        id: woId, site_id: siteId, vendor_id: vendorId, contract_nature: nature,
        title: title.trim(), scope_summary: scope.trim() || null,
        quotation_ref: quoteRef.trim() || null, quotation_date: quoteDate || null,
        start_date: startDate, completion_date: endDate,
        retention_pct: n(retention), defect_liability_months: Math.round(n(dlp)),
        advance_pct: n(advance), advance_recovery_pct: n(advRecovery),
        payment_terms: resolvedPaymentTerm || null,
        payment_term_id: chosenPaymentTerm?.id ?? null,
        payment_term_x: chosenPaymentTerm?.placeholder_label
          ? (paymentX.trim() || null) : null,
        internal_note: internalNote.trim() || null,
        created_by: profile!.id, creator_email: profile!.email,
      }
      const { error: hErr } = id
        ? await supabase.from('work_orders').update(header).eq('id', woId)
        : await supabase.from('work_orders').insert(header)
      if (hErr) throw new Error(hErr.message)

      /*
        THE LINES, IN ONE TRANSACTION — 0168.

        This used to be a DELETE followed by an INSERT, in two separate round
        trips, with the DELETE's result thrown away:

            await supabase.from('wo_line_items').delete().eq('wo_id', woId)
            const { error: lErr } = await supabase.from('wo_line_items').insert(...)

        Reported 21-09-2026 on WO-T12-20260921-001. It was raised with one
        line, returned twice, corrected twice, and ended up with THREE copies
        of line 1 and a contract value of ₹2,28,00,000 against an order for
        ₹76,00,000. Every correction added another copy, because the delete
        removed nothing and nobody was looking at the answer.

        The three rows all carried sl_no = 1, which is what proved it: this
        code writes sl_no as the array index plus one, so three rows numbered 1
        cannot be one save of a three-line form. They were three saves of a
        one-line form landing on top of each other.

        Two round trips cannot be made atomic by checking them harder, so the
        replacement now happens inside the database: rpc_wo_save_lines deletes
        and inserts in one statement block, refuses an order that has been
        signed, refuses to orphan a measurement, and RETURNS THE NUMBER OF
        LINES IT WROTE. A unique index on (wo_id, sl_no) makes the duplicate
        impossible whatever calls it.
      */
      const { data: written, error: lErr } = await supabase.rpc('rpc_wo_save_lines', {
        p_wo_id: woId,
        p_lines: lines.map((l) => ({
          pricing_basis: l.pricing_basis,
          // Every basis now carries its master item, not measured items alone.
          work_item_id: l.work_item_id || null,
          description: l.description.trim(),
          scope_text: l.scope_text.trim() || null,
          sac_code: l.sac_code || null,
          uom: l.uom.trim(),
          quantity: n(l.quantity), rate: n(l.rate),
          gst_rate_percent: n(l.gst_rate_percent),
          trade: l.pricing_basis === 'DAY_RATE' ? l.trade.trim() : null,
          headcount: l.pricing_basis === 'DAY_RATE' ? Math.round(n(l.headcount)) : null,
          // 0176: the schedule travels with the line, in the same call and
          // therefore in the same transaction. rpc_wo_save_lines deletes and
          // rewrites the lines wholesale and the milestones cascade with them,
          // so a schedule can never outlive the line it belonged to.
          milestones: l.pricing_basis === 'LUMPSUM'
            ? l.milestones.map((m) => ({ label: m.label.trim(), pct: n(m.pct) }))
            : [],
          area_basis: l.pricing_basis === 'AREA_RATE' ? l.area_basis.trim() : null,
          // 0214: a hire line's machines, quote and period. The database
          // works its days, day rate and unit out from these itself.
          ...(l.pricing_basis === 'EQUIPMENT_HIRE' ? {
            hire_units: Math.round(n(l.hire_units)),
            hire_rate_basis: l.hire_rate_basis,
            hire_quoted_rate: n(l.hire_quoted_rate),
            hire_period: n(l.hire_period),
            with_operator: l.with_operator,
            fuel_by_oval: l.fuel_by_oval,
          } : {}),
        })),
      })
      if (lErr) throw new Error(dbMessage(lErr, 'The lines could not be saved.'))

      // The count is the assurance the old two-step never gave anybody: the
      // order now holds exactly what was on the screen, and nothing else.
      if (Number(written) !== lines.length) {
        throw new Error(
          `The order was saved with ${written} line(s), but ${lines.length} `
          + `were on the screen. Nothing has been lost — open the order and `
          + `check its lines before submitting it.`)
      }

      // The contractor's papers. Uploaded AFTER the order exists, because the
      // policy that admits them checks that the doc_id is a work order this
      // person can reach - which it cannot be until there is one.
      //
      // A failure here does not lose the order. It is already saved; the person
      // is told which file did not go up and can attach it again on the work
      // order screen, where the same papers card is always available.
      if (papers.length) {
        try {
          await uploadWoPapers(woId!, papers)
          setPapers([])
        } catch (e) {
          toast.error(
            `${woId} was saved, but a paper did not upload: `
            + `${e instanceof Error ? e.message : 'unknown error'}. `
            + 'Attach it again from the work order screen.')
        }
      }

      // The standard clauses for the bases this order actually uses. Never
      // overwrites an edit: rpc_wo_apply_terms leaves an order that already has
      // terms exactly as it found it.
      try {
        await supabase.rpc('rpc_wo_apply_terms', { p_wo_id: woId, p_replace: false })
      } catch { /* the order is saved; terms can be pulled in from its own screen */ }

      if (thenSubmit) {
        const { error: sErr } = await supabase.rpc('rpc_works_action', {
          p_doc_type: 'WO', p_doc_id: woId, p_action: 'SUBMIT',
        })
        if (sErr) throw new Error(sErr.message)
        // 0217: an order raised by the site's Project Manager skips the
        // Procurement Manager's stage, so the message no longer names one.
        toast.success('Submitted for approval. The approval chain on the order shows who has it now.')
      } else {
        toast.success(`Saved as a draft — ${woId}. It is safe now.`)
      }
      if (!opts?.stay) navigate(`/service/work-orders/${woId}`)
      return woId!
    } catch (e) {
      const m = e instanceof Error ? e.message : 'Could not save the work order'
      setErr(m)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return null
    } finally {
      setBusy(false)
    }
  }

  if (!serviceCan(profile?.role, 'createWorkOrder')) {
    return <Alert tone="warning" title="Not open to your role">
      A work order is raised by Procurement, the Project Head, or the Project Manager
      of the site it is for.
    </Alert>
  }
  if (!allSites.length) return <Loading label="Loading sites…" />

  return (
    <div className="space-y-4">
      <button onClick={() => navigate('/service/work-orders')}
              className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> All work orders
      </button>

      <h1 className="text-xl font-bold text-slate-900">
        {editing ? `Edit ${id}` : 'New Work Order'}
      </h1>

      {err && <Alert tone="error" title="Not saved">{err}</Alert>}

      {/* ---------- who and what ---------- */}
      <Card title="The contract">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Site" required>
            <Select value={siteId} onChange={(e) => setSiteId(e.target.value)}
                    placeholder="Choose…"
                    options={mySites.map((s: any) => ({ value: s.id, label: s.site_name }))} />
          </Field>
          <Field label="Contractor" required>
            <Select value={vendorId} onChange={(e) => setVendorId(e.target.value)}
                    placeholder="Choose…"
                    options={contractors.map((v: any) => ({ value: v.id, label: v.company_name }))} />
          </Field>
          <Field label="Nature of contract"
                 hint={nature === 'EQUIPMENT_HIRE'
                   ? 'Machines or vehicles on hire, paid per day on site. Every line must be an equipment hire line.'
                   : natureClash
                   ?? (materialBasis === 'UNKNOWN'
                       ? 'Choose the work items and this is worked out from them.'
                       : `The lines say: ${{
                           LABOUR_ONLY: 'labour only',
                           WITH_MATERIALS: 'labour with materials',
                           MIXED: 'part with materials, part labour only',
                         }[materialBasis]}.`)}>
            {/* Select has no invalid prop; the hint above carries the message. */}
            <Select value={nature}
                    onChange={(e) => {
                      setNature(e.target.value)
                      // 0214: a line nobody has started yet follows the order
                      // into hire, so the buyer is not told to change it by hand.
                      if (e.target.value === 'EQUIPMENT_HIRE') {
                        setLines((ls) => ls.map((l) => (!l.work_item_id && !l.description.trim()
                          ? { ...l, pricing_basis: 'EQUIPMENT_HIRE' as Basis, uom: 'DAY' } : l)))
                      }
                    }}
                    options={[
                      { value: 'WORKS_CONTRACT', label: 'Works contract' },
                      { value: 'PURE_LABOUR', label: 'Pure labour, OVAL supplies material' },
                      { value: 'MANPOWER_SUPPLY', label: 'Manpower supply — OVAL directs the men' },
                      { value: 'EQUIPMENT_HIRE', label: 'Equipment / vehicle hire' },
                    ]} />
          </Field>
          <Field label="Title" required className="sm:col-span-2 lg:col-span-3"
                 hint="What a director would call this job">
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="Infrastructure development, G+2 school building, Blocks A B and C" />
          </Field>
          <Field label="Scope of work" className="sm:col-span-2 lg:col-span-3"
                 hint="Printed on the order. Say what is included and what is not.">
            <Textarea rows={3} value={scope} onChange={(e) => setScope(e.target.value)}
                      placeholder="Roof area measurement basis labour engagement. All material by OVAL." />
          </Field>
          <Field label="Quotation reference" hint="Never the word &ldquo;verbal&rdquo;">
            <Input value={quoteRef} onChange={(e) => setQuoteRef(e.target.value)}
                   placeholder="QT/JB/2026-27/018" />
          </Field>
          <Field label="Quotation date" hint="Cannot be in the future"
                 error={quoteDate && quoteDate > today
                   ? 'A quotation that has not arrived yet cannot be the one this order was priced from.'
                   : null}>
            <Input type="date" max={today} value={quoteDate}
                   invalid={Boolean(quoteDate && quoteDate > today)}
                   onChange={(e) => setQuoteDate(e.target.value)} />
          </Field>
          <div />
          <Field label="Start date" required hint="THE effective date">
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Field>
          <Field label="Completion date" required>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Field>
          {startDate && endDate && endDate >= startDate && (
            <div className="flex items-end pb-2 text-sm text-slate-500">
              {Math.round((+new Date(endDate) - +new Date(startDate)) / 86400000)} days
            </div>
          )}
        </div>

        {vendor && (
          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs">
            <p className="font-semibold text-slate-700">
              <Info className="mr-1 inline h-3.5 w-3.5" />
              What the system works out from this contractor
            </p>
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              <div>
                <p className="text-slate-500">TDS</p>
                <p className={tds === null ? 'font-semibold text-amber-700' : 'font-semibold text-slate-800'}>
                  {tds === null
                    ? 'No valid PAN — cannot be worked out'
                    : `${tds}% under s.393(1)`}
                </p>
                {vendor.pan && tds !== null && (
                  <p className="text-slate-400">
                    PAN {vendor.pan}, 4th character {vendor.pan[3]} ={' '}
                    {tds === 1 ? 'individual or HUF' : 'company, firm or other'}
                  </p>
                )}
              </div>
              <div>
                <p className="text-slate-500">GST</p>
                <p className="font-semibold text-slate-800">
                  {registered ? (interState ? 'IGST 18%' : 'CGST + SGST 18%') : 'Reverse charge'}
                </p>
                <p className="text-slate-400">
                  {registered
                    ? vendor.gstin
                    : 'Not registered — OVAL pays the 18% to the government, and it is NOT deducted from him'}
                </p>
              </div>
              <div>
                <p className="text-slate-500">Place of supply</p>
                <p className="font-semibold text-slate-800">{site?.state_name ?? '—'}</p>
                {interState && (
                  <p className="text-amber-700">
                    He is in {vendor.state_name}; the work is in {site?.state_name}. Inter-state.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* ---------- the lines ---------- */}
      <Card title="What he is being paid for"
            subtitle={nature === 'EQUIPMENT_HIRE'
              ? 'One line per kind of machine. Each is paid per day on site from the certified hire log sheet.'
              : 'A line may be priced any of four ways, and one order may mix them.'}
            actions={
              <button className="btn-secondary btn-sm"
                      onClick={() => setLines((ls) => [
                        nature === 'EQUIPMENT_HIRE'
                          ? { ...blank(), pricing_basis: 'EQUIPMENT_HIRE' as Basis, uom: 'DAY' }
                          : blank(),
                        ...ls])}>
                <Plus className="h-4 w-4" /> Add
              </button>
            }>
        <div className="space-y-4">
          {lines.map((l, i) => (
            <div key={l.key} className="rounded-lg border border-slate-200 p-3">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  Line {i + 1}
                </p>
                <button className="btn-ghost btn-sm text-red-600"
                        onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                        disabled={lines.length === 1}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Field label="Priced as" className="sm:col-span-2"
                       hint={BASIS.find((b) => b.value === l.pricing_basis)?.hint}>
                  <Select value={l.pricing_basis}
                          onChange={(e) => (e.target.value === 'EQUIPMENT_HIRE'
                            ? setHire(l.key, { pricing_basis: 'EQUIPMENT_HIRE' })
                            : setLine(l.key, { pricing_basis: e.target.value as Basis }))}
                          options={BASIS.map((b) => ({ value: b.value, label: b.label }))} />
                </Field>

                {/* THE PICKER IS FOR EVERY BASIS NOW, not measured items only.
                    Board, 16-09-2026. Until today an area-rate or lumpsum line
                    was typed free-hand, so nothing tied it to a rate, a
                    measurement rule or a SAC - which is why the first work
                    order PDF printed a blank SAC column. 0131 seeded area-rate
                    and lumpsum items into the schedule so there is always
                    something real to choose. */}
                {true && (
                  <Field label="Work item" required className="sm:col-span-2"
                         hint={l.work_item_id
                           ? (items as any[]).find((x) => x.id === l.work_item_id)?.measurement_rule
                             ?? 'Chosen from the schedule of rates'
                           : 'Opens the schedule of rates, where you can search all of it'}>
                    <button type="button" onClick={() => chooseWorkItem(l.key)}
                            className="field flex w-full items-center justify-between gap-2 text-left">
                      <span className={l.work_item_id ? 'truncate' : 'truncate text-slate-400'}>
                        {l.work_item_id
                          ? (() => {
                              const it = (items as any[]).find((x) => x.id === l.work_item_id)
                              return it ? `${it.item_name} · ${it.uom}` : l.work_item_id
                            })()
                          : 'Choose from the schedule of rates…'}
                      </span>
                      <Search className="h-4 w-4 shrink-0 text-slate-400" />
                    </button>
                  </Field>
                )}
                {l.pricing_basis === 'AREA_RATE' && (
                  <Field label="Which area" required className="sm:col-span-2"
                         hint="Roof area, built-up, carpet — say so on the order">
                    <Input value={l.area_basis} onChange={(e) => setLine(l.key, { area_basis: e.target.value })}
                           placeholder="Roof area, G+2, Blocks A B and C" />
                  </Field>
                )}
                {l.pricing_basis === 'DAY_RATE' && (
                  <>
                    <Field label="Trade" required
                           hint="From the trade master. Add one if it is missing.">
                      <Select value={l.trade}
                              placeholder="Choose the trade…"
                              onChange={(e) => {
                                if (e.target.value === ADD_NEW) {
                                  setNewTradeFor(l.key); setNewTradeName('')
                                } else {
                                  setLine(l.key, { trade: e.target.value })
                                }
                              }}
                              options={[
                                ...(trades as any[]).map((t) => ({
                                  value: t.trade_name, label: t.trade_name,
                                })),
                                { value: ADD_NEW, label: '＋ Add a new trade…' },
                              ]} />
                    </Field>
                    <Field label="Headcount" required>
                      <Input type="number" min="1" value={l.headcount}
                             onChange={(e) => setLine(l.key, { headcount: e.target.value })} />
                    </Field>
                  </>
                )}
                {l.pricing_basis === 'EQUIPMENT_HIRE' && (() => {
                  // 0220: formwork, props and the like are hired BY THE PIECE -
                  // logged on delivery and return challans; no operator, no fuel.
                  const pieces = Boolean((items as any[]).find((x) => x.id === l.work_item_id)?.hire_by_pieces)
                  const unit = pieces ? 'piece' : 'machine'
                  return (
                  <>
                    {/* 0214. What the vendor quoted, and for how long. The
                        days, day rate and amount below follow from these. */}
                    <Field label={pieces ? 'Pieces' : 'Machines'} required
                           hint={pieces ? 'How many pieces are being hired' : 'How many of this machine'}>
                      <Input type="number" min="1" step="1" value={l.hire_units}
                             onChange={(e) => setHire(l.key, { hire_units: e.target.value })} />
                    </Field>
                    <Field label="Quoted per" required>
                      <Select value={l.hire_rate_basis}
                              onChange={(e) => setHire(l.key, { hire_rate_basis: e.target.value })}
                              options={[{ value: 'MONTH', label: 'Month' }, { value: 'DAY', label: 'Day' }]} />
                    </Field>
                    <Field label={`Hire rate per ${l.hire_rate_basis === 'MONTH' ? 'month' : 'day'}`} required
                           hint={`As quoted, for one ${unit}`}>
                      <Input type="number" min="0.01" step="0.01" value={l.hire_quoted_rate}
                             onChange={(e) => setHire(l.key, { hire_quoted_rate: e.target.value })} />
                    </Field>
                    <Field label={`Expected period (${l.hire_rate_basis === 'MONTH' ? 'months' : 'days'})`} required
                           hint="Sets the order value. Longer needs a variation.">
                      <Input type="number" min="0.5" step="0.5" value={l.hire_period}
                             onChange={(e) => setHire(l.key, { hire_period: e.target.value })} />
                    </Field>
                    {!pieces && (
                      <>
                        <Field label="Operator">
                          <Select value={l.with_operator ? 'Y' : 'N'}
                                  onChange={(e) => setHire(l.key, { with_operator: e.target.value === 'Y' })}
                                  options={[{ value: 'Y', label: 'With operator' },
                                            { value: 'N', label: 'Without operator' }]} />
                        </Field>
                        <Field label="Fuel">
                          <Select value={l.fuel_by_oval ? 'Y' : 'N'}
                                  onChange={(e) => setHire(l.key, { fuel_by_oval: e.target.value === 'Y' })}
                                  options={[{ value: 'Y', label: 'Fuel by OVAL' },
                                            { value: 'N', label: 'Fuel by the contractor' }]} />
                        </Field>
                      </>
                    )}
                    <p className="sm:col-span-2 flex items-end pb-2 text-xs text-slate-500">
                      {n(l.quantity) > 0 && n(l.rate) > 0
                        ? `${n(l.hire_units)} × ${n(l.hire_period)} ${l.hire_rate_basis === 'MONTH' ? 'month(s) × 30' : 'day(s)'} = ${n(l.quantity)} ${unit}-days at ${rupees(n(l.rate))} a day`
                          + (l.hire_rate_basis === 'MONTH' ? ' (the monthly rate ÷ 30)' : '')
                          + (pieces ? '. Logged on delivery and return challans.' : '')
                        : `Fill in ${unit}s, rate and period to see the days and the day rate.`}
                    </p>
                  </>
                  )
                })()}
                {l.pricing_basis === 'LUMPSUM' && (
                  <MilestoneSchedule
                    line={l}
                    lineValue={n(l.quantity) * n(l.rate)}
                    total={msTotal(l)}
                    onChange={(ms) => setLine(l.key, { milestones: ms })} />
                )}

                <Field label="Description" required className="sm:col-span-2"
                       hint="The schedule's own wording. Elaborate underneath rather than rewriting it.">
                  <Input value={l.description}
                         onChange={(e) => setLine(l.key, { description: e.target.value })} />
                </Field>

                {/* WHAT IS ACTUALLY INCLUDED ON THIS ORDER.
                    Every one of the company's historic work orders carries this
                    in prose - "all work to be completed as per our drawing &
                    guidance by site engineer & this scope of work will be
                    without material" - and the system had nowhere to put it, so
                    people were overwriting the schedule's description instead
                    and two orders for the same item stopped reading alike. */}
                <Field label="Scope of this line" className="sm:col-span-4"
                       hint="Free text. What is included, what is excluded, whose material, which drawing.">
                  <textarea rows={2} className="field w-full"
                            value={l.scope_text}
                            onChange={(e) => setLine(l.key, { scope_text: e.target.value })}
                            placeholder="Without material. Reinforcement and cement issued by OVAL against MIN. As per drawing SD-114 Rev 2." />
                </Field>

                <Field label="SAC" required className="sm:col-span-2"
                       hint={l.sac_code
                         ? (sacs as any[]).find((x) => x.code === l.sac_code)?.description
                         : 'Comes from the work item. Change it only if this line is really a different service.'}>
                  <Select value={l.sac_code}
                          onChange={(e) => pickSac(l.key, e.target.value)}
                          placeholder="Choose a SAC code"
                          options={(sacs as any[]).map((x) => ({
                            value: x.code,
                            label: `${x.code} — ${String(x.description).slice(0, 60)}`,
                          }))} />
                </Field>
                <Field label="Unit" required
                       hint={l.uom && !uomOptions.some((u) => u.value === l.uom)
                         ? `"${l.uom}" is not in the UOM master`
                         : undefined}>
                  <Select value={l.uom} onChange={(e) => setLine(l.key, { uom: e.target.value })}
                          disabled={l.pricing_basis === 'EQUIPMENT_HIRE'}
                          placeholder="Choose…" options={uomOptions} />
                </Field>
                {/* On a hire line these three are worked out, not typed (0214). */}
                <Field label={l.pricing_basis === 'EQUIPMENT_HIRE' ? 'Machine-days' : 'Quantity'} required>
                  <Input type="number" step="0.001" min="0.001" value={l.quantity}
                         invalid={l.quantity !== '' && n(l.quantity) <= 0}
                         disabled={l.pricing_basis === 'EQUIPMENT_HIRE'}
                         onChange={(e) => setLine(l.key, { quantity: e.target.value })} />
                </Field>
                <Field label={l.pricing_basis === 'EQUIPMENT_HIRE' ? 'Day rate' : 'Rate'} required>
                  <Input type="number" step="0.01" min="0.01" value={l.rate}
                         disabled={l.pricing_basis === 'EQUIPMENT_HIRE'}
                         onChange={(e) => setLine(l.key, { rate: e.target.value })} />
                </Field>
                <Field label="GST %">
                  <Input type="number" step="0.01" value={l.gst_rate_percent}
                         onChange={(e) => setLine(l.key, { gst_rate_percent: e.target.value })} />
                </Field>
                <div className="flex items-end pb-2">
                  <p className="text-sm">
                    <span className="text-slate-500">Amount </span>
                    <span className="font-semibold tabular-nums text-slate-800">
                      {rupees(n(l.quantity) * n(l.rate))}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 rounded-lg bg-slate-50 px-4 py-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm text-slate-600">Contract value</p>
            <p className="text-lg font-bold tabular-nums text-slate-900">{rupees(money.value)}</p>
          </div>
          <div className="mt-1 flex flex-wrap items-baseline justify-between gap-2 text-sm">
            <p className="text-slate-600">
              GST {registered ? '' : '(reverse charge — OVAL pays it, not him)'}
            </p>
            <p className="tabular-nums text-slate-700">{rupees(money.gst)}</p>
          </div>
          <div className="mt-1 flex flex-wrap items-baseline justify-between gap-2 border-t border-slate-200 pt-1">
            <p className="text-sm font-semibold text-slate-700">Total</p>
            <p className="font-bold tabular-nums text-slate-900">{rupees(money.total)}</p>
          </div>
          {/*
            0176: one summary line per lumpsum LINE, not one for the order.
            The old single figure added every milestone on the order together,
            which only made sense while a line could hold just one of them.
          */}
          {lines.filter((l) => l.pricing_basis === 'LUMPSUM').map((l, i) => {
            const t = msTotal(l)
            const balanced = l.milestones.length > 0 && Math.abs(t - 100) < 0.001
            const at = lines.indexOf(l) + 1
            return (
              <p key={l.key} className={balanced
                ? 'mt-2 text-xs text-emerald-700'
                : 'mt-2 text-xs font-semibold text-red-600'}>
                Line {at} milestones total {Number(t.toFixed(3))}%
                {balanced ? ' ✓' : ' — must be 100%'}
                {i === 0 && !balanced && <span className="block font-normal text-slate-400">
                  Each lumpsum line is released by its own stages.
                </span>}
              </p>
            )
          })}
        </div>
      </Card>

      {/* ---------- deductions ---------- */}
      <Card title="Deductions, agreed once"
            subtitle="Applied to every running account bill. Nobody types them per bill.">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Retention %" hint="Held from each bill">
            <Input type="number" step="0.01" min="0" max="100" value={retention}
                   onChange={(e) => setRetention(e.target.value)} />
          </Field>
          <Field label="Defect liability (months)" hint="Retention released after this">
            <Input type="number" min="0" value={dlp} onChange={(e) => setDlp(e.target.value)} />
          </Field>
          <Field label="Mobilisation advance %" hint="0 if none">
            <Input type="number" step="0.01" min="0" max="100" value={advance}
                   onChange={(e) => setAdvance(e.target.value)} />
          </Field>
          <Field label="Advance recovery % per bill">
            <Input type="number" step="0.01" min="0" max="100" value={advRecovery}
                   onChange={(e) => setAdvRecovery(e.target.value)} />
          </Field>
        </div>
        {/* ----------------------------------------------------------------
            WHEN HE GETS PAID — 0171.

            The same master Material Procurement reads, filtered to what a
            works contract can honour. A work order for a crore used to go out
            saying nothing at all about this.
        ---------------------------------------------------------------- */}
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <Field label="Payment terms" className="sm:col-span-2"
                 hint="Printed on the order. Works-contract terms only — delivery and goods-receipt terms belong on a purchase order.">
            <Select value={paymentTerm}
                    onChange={(e) => { setPaymentTerm(e.target.value); setPaymentX('') }}
                    placeholder="Not stated"
                    options={(paymentTerms as any[]).map((t) => ({
                      value: t.term_text, label: t.term_text,
                    }))} />
          </Field>
          {chosenPaymentTerm?.placeholder_label && (
            <Field label={String(chosenPaymentTerm.placeholder_label)} required
                   hint="Replaces the X before it is printed.">
              <Input value={paymentX} onChange={(e) => setPaymentX(e.target.value)} />
            </Field>
          )}
        </div>

        {resolvedPaymentTerm && (
          <p className="mt-1 text-xs text-slate-500">
            Printed as: <strong className="text-slate-700">{resolvedPaymentTerm}</strong>
          </p>
        )}

        {termClash && (
          <div className="mt-2 rounded-lg border border-amber-200 border-l-4 border-l-amber-600 bg-amber-50 px-3 py-2
                          text-xs text-amber-800">
            <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
            {termClash}
          </div>
        )}

        <Field label="Internal note" className="mt-4"
               hint="NEVER printed on the work order. OVAL's note to itself.">
          <Textarea rows={2} value={internalNote}
                    onChange={(e) => setInternalNote(e.target.value)} />
        </Field>
      </Card>

      {/* ---------- the contractor's papers ---------- */}
      {/*
        WHY THERE ARE TWO THINGS HERE WHEN EDITING.

        The collector below holds files that have NOT gone up yet — they are
        still on this computer and are uploaded when you press Save. The card
        under it lists what is ALREADY on the order, on the server.

        Testing on 14-09-2026 found the hole: attach two files, save the draft,
        reopen it to edit, and the collector was empty — correctly, because
        those files were no longer pending. But nothing on the edit screen
        showed them either, so they looked lost. They were not; they were on the
        order screen the whole time. Now both screens show them.
      */}
      <Card title="Papers behind this order"
            subtitle="What was collected before deciding to place it. Attached to the order, not to the vendor master, so the file shows what was held on the day it was signed.">
        <AttachmentCollector
          docType="WO"
          value={papers}
          onChange={setPapers}
          hint={'The quotation the rates came from, PAN, GST and bank details, MSME and Udyam, '
                + 'labour licence, insurance, test and experience certificates. '
                + 'They go up when you press Save. The signed copy is attached later, '
                + 'on the order itself, once the contractor has acknowledged it.'}
        />
        {papers.length > 0 && (
          <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {papers.length} file{papers.length === 1 ? '' : 's'} waiting on this computer.
            They are uploaded when you press <strong>Save as draft</strong> or{' '}
            <strong>Save and submit</strong> — not before.
          </p>
        )}
      </Card>

      {editing && <WoPapers woId={id!} status={savedStatus} />}

      {/*
        TERMS AND CONDITIONS. Only once the order has a number - a clause has to
        hang off something. Saving as a draft pulls the standard set in for the
        pricing bases this order actually uses, and it is editable from there
        until the order is submitted.
      */}
      {editing ? (
        <WoTerms woId={id!} editable={['DRAFT', 'RETURNED'].includes(savedStatus)} />
      ) : (
        <Card title="Terms and conditions"
              subtitle="The standard clauses for this order's pricing bases.">
          <p className="rounded-lg border border-dashed border-slate-300 px-3 py-2.5 text-sm text-slate-600">
            They are brought in when you <strong>Save as draft</strong> — the right set depends
            on how the lines above are priced, so the order needs to exist first. You can then
            edit, add or delete any clause until it is submitted.
          </p>
        </Card>
      )}

      {/* ---------- what is stopping you ---------- */}
      {problems.length > 0 && (
        <Card title={`${problems.length} thing${problems.length === 1 ? '' : 's'} to fix before this can be submitted`}>
          <ul className="space-y-1.5">
            {problems.map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="flex flex-wrap gap-2">
        <button className="btn-secondary" disabled={busy} onClick={() => void save(false)}>
          Save as draft
        </button>
        <button className="btn-primary" disabled={busy || problems.length > 0}
                onClick={() => void save(true)}>
          {busy ? 'Working…' : 'Save and submit for approval'}
        </button>
      </div>

      {/* ---------- leaving an unsaved form ---------- */}
      {/* Adding a trade the master has never seen. A modal rather than an
          inline text box, because this writes to a MASTER that every other
          order will read — that deserves a deliberate act, not a stray
          keystroke in a field somebody meant to search. */}
      <Modal open={newTradeFor !== null} onClose={() => setNewTradeFor(null)}
             title="Add a trade"
             subtitle="It is added to the trade master, so every later work order can pick it too."
             footer={
               <>
                 <button className="btn-secondary" onClick={() => setNewTradeFor(null)}>
                   Cancel
                 </button>
                 <button className="btn-primary" disabled={busy || !newTradeName.trim()}
                         onClick={() => void saveNewTrade()}>
                   {busy ? 'Adding…' : 'Add it'}
                 </button>
               </>
             }>
        <Field label="Trade" required
               hint="How it should read on every future order — 'Rigger', not 'rigger 2 nos'.">
          <Input value={newTradeName} autoFocus
                 onChange={(e) => setNewTradeName(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') void saveNewTrade() }}
                 placeholder="Rigger" />
        </Field>
      </Modal>

      <Modal
        open={leaveTo !== null}
        onClose={() => setLeaveTo(null)}
        title="This order has not been saved yet"
        subtitle="Choosing a work item opens the Schedule of Rates, which means leaving this page."
        footer={
          <>
            <button className="btn-ghost" onClick={() => setLeaveTo(null)}>
              Stay here
            </button>
            <button className="btn-secondary" disabled={busy}
                    onClick={() => { const k = leaveTo; setLeaveTo(null); if (k) goToPicker(k) }}>
              Go without saving
            </button>
            <button className="btn-primary" disabled={busy || problems.length > 0}
                    onClick={() => void saveThenChoose()}>
              {busy ? 'Saving…' : 'Save the draft first, then choose'}
            </button>
          </>
        }>
        <div className="space-y-3 text-sm text-slate-700">
          <div className="rounded-lg border border-amber-300 border-l-4 border-l-amber-600 bg-amber-50 px-3 py-2">
            <p className="font-semibold text-amber-900">
              What you have typed is carried across and comes back with you —
              including the files you have attached.
            </p>
            <p className="mt-1 text-xs text-amber-800">
              That is true whether you pick an item or press <em>Back without choosing</em>.
            </p>
          </div>
          <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2">
            <p className="font-semibold text-red-900">
              But it is held in this browser tab only, and nowhere else.
            </p>
            <p className="mt-1 text-xs text-red-800">
              Press <strong>F5</strong>, reload, close the tab, or let the session drop while
              you are on the Schedule of Rates, and this whole form is gone. There is nothing
              on the server to come back to yet.
            </p>
          </div>
          <p>
            <strong>Save the draft first</strong> and none of that can touch it — the order
            gets its number, the files go up, and you carry on editing it. It is one extra
            click and it is the safe one.
          </p>
          {problems.length > 0 && (
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
              It cannot be saved as it stands — there {problems.length === 1 ? 'is' : 'are'}{' '}
              {problems.length} thing{problems.length === 1 ? '' : 's'} listed at the foot of
              the form to fix first. Until then, <strong>Go without saving</strong> is your
              only way to the Schedule of Rates.
            </p>
          )}
        </div>
      </Modal>
    </div>
  )
}
